import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, DollarSign, MessageCircle, Phone } from "lucide-react";
import { brl, dt } from "@/lib/format";

export default function AtrasadosDialog({ open, onOpenChange, clients, onPay, onOpenClient }) {
  const [q, setQ] = useState("");

  const atrasados = useMemo(() => {
    const list = (clients || []).filter((c) => c.status === "atrasado");
    return list.sort((a, b) => {
      const av = new Date(`${a.next_due_date}T12:00:00`).getTime();
      const bv = new Date(`${b.next_due_date}T12:00:00`).getTime();
      return av - bv;
    });
  }, [clients]);

  const filtered = useMemo(() => {
    if (!q) return atrasados;
    const s = q.toLowerCase();
    return atrasados.filter(
      (c) => c.name.toLowerCase().includes(s) || (c.phone || "").includes(q),
    );
  }, [atrasados, q]);

  const totalOverdue = atrasados.reduce(
    (sum, c) => sum + (c.total_due_today || c.next_interest || 0),
    0,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0B132B] border-slate-800 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="atrasados-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2 text-xl"
            style={{ fontFamily: "Outfit" }}
          >
            <AlertTriangle className="w-5 h-5 text-rose-400" /> Clientes em atraso
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Total a receber hoje:{" "}
            <span className="font-mono text-rose-300 font-bold">{brl(totalOverdue)}</span> ·{" "}
            {atrasados.length} cliente(s) em atraso
          </DialogDescription>
        </DialogHeader>

        <div className="my-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome ou telefone..."
            className="w-full h-10 rounded-lg bg-slate-900/60 border border-slate-700 px-3 text-sm focus:border-blue-500 outline-none"
            data-testid="atrasados-search"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="py-14 text-center text-slate-500">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {atrasados.length === 0 ? "Ninguém em atraso — parabéns!" : "Nenhum resultado."}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((c) => {
              const waPhone = (c.phone || "").replace(/\D/g, "");
              return (
                <div
                  key={c.id}
                  className="rounded-xl border border-rose-500/25 bg-rose-500/5 p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid={`atrasados-row-${c.id}`}
                >
                  <button
                    type="button"
                    onClick={() => onOpenClient?.(c)}
                    className="flex-1 min-w-0 text-left hover:opacity-90"
                  >
                    <div className="font-bold text-slate-100 truncate">{c.name}</div>
                    <div className="text-xs text-slate-400 flex items-center gap-3 flex-wrap mt-0.5">
                      {c.phone && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {c.phone}
                        </span>
                      )}
                      <span className="text-rose-300">
                        Venceu em {dt(c.next_due_date)} · {c.days_past_due || 0} dia(s) em atraso
                      </span>
                    </div>
                  </button>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">
                      Devido hoje
                    </div>
                    <div className="text-lg font-mono text-rose-300 font-bold">
                      {brl(c.total_due_today || c.next_interest || 0)}
                    </div>
                    {(c.late_fee || 0) > 0 && (
                      <div className="text-[10px] text-rose-400/80 font-mono">
                        multa {brl(c.late_fee)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {waPhone && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 h-9"
                        onClick={() => {
                          const business = "MS Soluções Financeiras";
                          const msg = `Olá ${c.name}, aqui é da ${business}. Sua parcela venceu em ${dt(c.next_due_date)} (${c.days_past_due || 0} dia(s) em atraso). Valor atualizado hoje: ${brl(c.total_due_today || c.next_interest || 0)}. Podemos ajustar o pagamento?`;
                          window.open(
                            `https://wa.me/55${waPhone}?text=${encodeURIComponent(msg)}`,
                            "_blank",
                            "noopener",
                          );
                        }}
                        data-testid={`atrasados-remind-${c.id}`}
                      >
                        <MessageCircle className="w-3.5 h-3.5 mr-1" /> Lembrar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-500 text-white h-9"
                      onClick={() => onPay?.(c)}
                      data-testid={`atrasados-pay-${c.id}`}
                    >
                      <DollarSign className="w-3.5 h-3.5 mr-1" /> Pagar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
